import type { Session } from "../core/session";
import { takeBlob } from "../store/takes";
import type { Take } from "../core/types";
import { el } from "../ui/dom";

/**
 * Embed protocol between the HMM portal (host) and the recorder (iframe).
 *
 * The host never receives the video through postMessage — a lab video is far
 * too large for that. It hands over a presigned R2 URL before the take, the
 * recorder PUTs the finished file straight to storage, and only the object key
 * travels back. Storage credentials stay in the portal backend; the iframe
 * only ever sees one short-lived URL.
 *
 * Origins: the recorder answers only the origin named in `?host=`, and posts
 * only to it. No wildcard, no `*`.
 */

const PROTOCOL = 1;

export interface UploadTarget {
  /** Where the file goes: a presigned R2 URL or a Stream direct-upload URL. */
  url: string;
  headers?: Record<string, string>;
  /** Object key or Stream uid the host expects back. */
  key: string;
  /** Stream's direct upload takes a multipart POST, R2 takes a raw PUT. */
  method?: "PUT" | "POST";
  /** When set, the file is sent as multipart under this field name. */
  formField?: string;
}

/** The portal's own origin, plus local development. Nothing else may embed. */
const ALLOWED_HOSTS = [
  "https://portal.hermoodmentor.com",
  "https://courses.hermoodmentor.com",
  "https://hmm-course-portal.pages.dev",
  "http://127.0.0.1:8788",
  "http://127.0.0.1:8901",
];

interface HostMessage {
  v?: number;
  type?: string;
  id?: string;
  upload?: UploadTarget;
  options?: {
    camera?: boolean;
    mic?: boolean;
    systemAudio?: boolean;
    quality?: { height: 720 | 1080 | 1440; fps: 30 | 60 };
  };
}

export interface EmbedConfig {
  hostOrigin: string;
}

export function embedConfig(location: Location): EmbedConfig | null {
  const params = new URL(location.href).searchParams;
  if (params.get("embed") !== "1") return null;
  const host = params.get("host");
  if (!host) return null;
  try {
    const origin = new URL(host).origin;
    return ALLOWED_HOSTS.includes(origin) ? { hostOrigin: origin } : null;
  } catch {
    return null;
  }
}

export function mountEmbedBridge(session: Session, config: EmbedConfig): void {
  const { hostOrigin } = config;
  let upload: UploadTarget | null = null;
  let recovering = false;
  let lastTick = 0;

  document.body.dataset.embed = "1";

  const post = (message: Record<string, unknown>): void => {
    window.parent.postMessage({ v: PROTOCOL, ...message }, hostOrigin);
  };

  const fail = (message: string, code = "error"): void => {
    post({ type: "record:error", code, message });
  };

  async function prepareStorage(): Promise<void> {
    // No folder picker inside a portal: OPFS is the local write target, and
    // the journal keeps the take if the tab dies before the upload lands.
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle("embed-takes", { create: true });
    session.useFolder({ handle: dir, name: "Portal recording" });
  }

  /** PUTs a blob to whichever storage the host armed. */
  async function putFile(blob: Blob, target: UploadTarget): Promise<void> {
    if (target.formField) {
      const form = new FormData();
      form.append(target.formField, blob, "video.mp4");
      // No content-type header: the browser must set the multipart boundary.
      const response = await fetch(target.url, {
        method: target.method ?? "POST",
        headers: target.headers ?? {},
        body: form,
      });
      if (!response.ok) throw new Error(`storage said ${response.status} ${await response.text().catch(() => "")}`.trim());
      return;
    }
    const response = await fetch(target.url, {
      method: target.method ?? "PUT",
      headers: target.headers ?? {},
      body: blob,
    });
    if (!response.ok) throw new Error(`storage said ${response.status} ${await response.text().catch(() => "")}`.trim());
  }

  async function dropLocal(fileName: string): Promise<void> {
    try {
      const dir = await navigator.storage.getDirectory().then((r) => r.getDirectoryHandle("embed-takes"));
      await dir.removeEntry(fileName);
    } catch {
      // Keeping a stray local copy is harmless.
    }
  }

  async function uploadTake(take: Take, target: UploadTarget): Promise<void> {
    post({ type: "record:uploading", bytes: take.bytes, key: target.key });
    try {
      await putFile(await takeBlob(take), target);
      post({
        type: "record:saved",
        take: {
          key: target.key,
          fileName: take.fileName,
          bytes: take.bytes,
          durationMs: take.durationMs,
          width: take.width,
          height: take.height,
          mime: take.mime,
        },
      });
      // The bytes are in storage; the target is spent, and so is the copy.
      upload = null;
      await dropLocal(take.fileName);
    } catch (error) {
      // The take stays on disk; the next arm retries it.
      post({
        type: "record:error",
        code: "upload",
        message: error instanceof Error ? error.message : String(error),
        kept: true,
      });
    }
  }

  /** Files left behind by a failed upload, retried whenever a target is armed. */
  async function retryLocal(target: UploadTarget): Promise<void> {
    let dir: FileSystemDirectoryHandle;
    try {
      dir = await navigator.storage.getDirectory().then((r) => r.getDirectoryHandle("embed-takes"));
    } catch {
      return;
    }
    for await (const [name, handle] of dir.entries()) {
      if (handle.kind !== "file") continue;
      try {
        const file = await (handle as FileSystemFileHandle).getFile();
        if (file.size === 0) {
          await dir.removeEntry(name);
          continue;
        }
        const extension = name.split(".").pop() ?? "mp4";
        const stamp = name.replace(/[^0-9]/g, "").slice(-8) || crypto.randomUUID().slice(0, 8);
        const key = target.key.replace(/\.[^.]+$/, `-retry-${stamp}.${extension}`);
        await putFile(file, { ...target, key });
        post({ type: "record:recovered", session: name, key });
        await dir.removeEntry(name);
      } catch (error) {
        post({
          type: "record:error",
          code: "upload",
          message: error instanceof Error ? error.message : String(error),
          kept: true,
        });
      }
    }
  }

  /**
   * A take interrupted before the upload landed is still in the journal. On
   * arming, push those up under a derived key before the next take starts.
   */
  async function recoverPending(target: UploadTarget): Promise<void> {
    if (session.isRecording || session.phase === "arming" || session.phase === "finalizing") return;
    let pending: { session: string; extension: "mp4" | "webm" }[] = [];
    try {
      pending = await session.listUnfinished();
    } catch {
      return;
    }
    for (const entry of pending) {
      try {
        recovering = true;
        const take = await session.recover(entry.session, entry.extension);
        recovering = false;
        if (!take) continue;
        const extension = take.fileName.split(".").pop() ?? "mp4";
        const key = target.key.replace(/\.[^.]+$/, `-recovered-${take.id.slice(0, 8)}.${extension}`);
        await putFile(await takeBlob(take), { ...target, key });
        post({ type: "record:recovered", session: entry.session, key });
        await dropLocal(take.fileName);
      } catch (error) {
        post({
          type: "record:error",
          code: "recover",
          message: error instanceof Error ? error.message : String(error),
          kept: true,
        });
      }
    }
  }

  session.events.on("phase", (phase) => post({ type: "record:phase", phase }));
  session.events.on("tick", (elapsedMs) => {
    const now = Date.now();
    if (now - lastTick < 1000) return;
    lastTick = now;
    post({ type: "record:tick", elapsedMs });
  });
  session.events.on("saved", (take) => {
    // Recovery uploads itself under its own key; uploading here would double it.
    if (recovering) return;
    if (upload) void uploadTake(take, upload);
    else {
      post({
        type: "record:error",
        code: "no-target",
        message: "No upload target was armed for this take.",
        kept: true,
      });
    }
  });
  session.events.on("failed", (error) => fail(error.message, error.code));
  session.events.on("notice", ({ message, kind }) => {
    if (kind === "error") fail(message, "notice");
  });

  window.addEventListener("message", (event) => {
    if (event.origin !== hostOrigin) return;
    const message = event.data as HostMessage | null;
    if (!message || typeof message !== "object" || message.v !== PROTOCOL) return;
    const type = message.type ?? "";

    if (type === "record:ping") {
      post({ type: "record:ready", capabilities: capabilities() });
      return;
    }

    if (type === "record:arm") {
      if (!message.upload?.url || !message.upload.key) {
        fail("Arming needs an upload url and key.", "arm");
        return;
      }
      upload = message.upload;
      void (async () => {
        await recoverPending(upload!);
        await retryLocal(upload!);
        post({ type: "record:ack", id: message.id, armed: true });
      })();
      return;
    }

    if (type === "record:disarm") {
      upload = null;
      post({ type: "record:ack", id: message.id, armed: false });
      return;
    }

    if (type === "record:start") {
      void (async () => {
        if (!upload) {
          fail("Arm an upload target before starting.", "no-target");
          return;
        }
        try {
          const options = message.options ?? {};
          if (options.quality) session.setQuality(options.quality);
          if (options.camera !== undefined) await session.setCamera(options.camera);
          if (options.mic !== undefined) await session.setMic(options.mic);
          if (options.systemAudio !== undefined) session.setSystemAudio(options.systemAudio);
          if (session.screenEntries.length === 0 && !session.hasCamera) {
            fail("Nothing to record: share a screen or turn the camera on.", "no-source");
            return;
          }
          await session.start();
          post({ type: "record:ack", id: message.id, started: true });
        } catch (error) {
          fail(error instanceof Error ? error.message : String(error), "start");
        }
      })();
      return;
    }

    if (type === "record:stop") {
      void (async () => {
        try {
          await session.stop();
          post({ type: "record:ack", id: message.id, stopped: true });
        } catch (error) {
          fail(error instanceof Error ? error.message : String(error), "stop");
        }
      })();
      return;
    }

    if (type === "record:add-screen") {
      void (async () => {
        try {
          await session.addScreen();
          post({ type: "record:ack", id: message.id, screens: session.screenEntries.length });
        } catch (error) {
          fail(error instanceof Error ? error.message : String(error), "screen");
        }
      })();
    }
  });

  void prepareStorage()
    .then(() => post({ type: "record:ready", capabilities: capabilities() }))
    .catch((error) => fail(String(error), "storage"));

  // Embed mode has one view: the portal owns history and settings.
  for (const view of document.querySelectorAll<HTMLElement>("#view-library, #view-settings")) view.hidden = true;
  const tabs = document.querySelector<HTMLElement>(".tabs");
  if (tabs) tabs.hidden = true;
  el<HTMLElement>("view-record").hidden = false;
}

function capabilities(): Record<string, unknown> {
  const mimes = [
    'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
    'video/mp4;codecs="avc1.640028,mp4a.40.2"',
    "video/mp4",
  ];
  return {
    protocol: PROTOCOL,
    mp4: mimes.filter((mime) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mime)),
    webcodecs: typeof VideoEncoder !== "undefined",
    opfs: typeof navigator.storage?.getDirectory === "function",
  };
}
